// src/components/NotificationGenie.tsx
//
// macOS-dock "genie" dismissal animation for Sonner toasts.
//
// When ANY Sonner toast is dismissed (auto-close, X, or programmatic), Sonner
// flips `data-removed="true"` on the toast <li data-sonner-toast> and keeps it
// mounted for its own ~500ms exit transition before unmounting. We intercept
// that instant: hide the original, deep-clone it into a fixed overlay, and fly
// the clone along a curved path while tapering its width (the "suck") into the
// Notifications bell button — then pulse the bell. Pure transform/opacity
// (GPU-friendly), dependency-free, leak-free, StrictMode-safe.
//
// Graceful no-ops: when the bell is absent (e.g. /login) or covered (the bell
// panel is open), when geometry is degenerate, or when the user prefers
// reduced motion — Sonner's plain fade takes over. Swipe-dismiss is left to
// Sonner's own swipe-out so we don't hijack a gesture mid-flight.
//
// Mount ONCE, beside <SonnerToaster/> in App.js:  <NotificationGenie />
import { useEffect } from "react";

// ---------------------------------------------------------------------------
// Tunables / selectors
// ---------------------------------------------------------------------------
const TOAST_SELECTOR = "li[data-sonner-toast]";
const BELL_SELECTOR = 'button[aria-label="Notifications"]';
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

const FLIGHT_MS = 540;   // toast -> bell flight
const PULSE_MS = 340;    // bell bounce on landing
const ARC_SAMPLES = 10;  // bezier keyframe samples
const ARC_BOW = 0.5;     // outward bow of the curved path (fraction of |dx|)
const OVERLAY_ID = "notification-genie-overlay";
const FLOWN_ATTR = "data-genie-flown"; // marks a real <li> as handled

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(REDUCED_MOTION).matches
  );
}

function quadraticPoint(
  t: number,
  p0: { x: number; y: number },
  pc: { x: number; y: number },
  p1: { x: number; y: number }
): { x: number; y: number } {
  const mt = 1 - t;
  return {
    x: mt * mt * p0.x + 2 * mt * t * pc.x + t * t * p1.x,
    y: mt * mt * p0.y + 2 * mt * t * pc.y + t * t * p1.y,
  };
}

// Is `el` the topmost thing at viewport point (cx, cy)? Used to skip the genie
// when the bell is covered (e.g. the notifications modal backdrop is open).
// Overlay/clones use pointer-events:none, so elementFromPoint ignores them.
function isPointOnElement(el: Element, cx: number, cy: number): boolean {
  if (cx < 0 || cy < 0 || cx > window.innerWidth || cy > window.innerHeight) return false;
  const hit = document.elementFromPoint(cx, cy);
  return !!hit && (hit === el || el.contains(hit));
}

// ---------------------------------------------------------------------------
// Module-level refcounted singleton. React StrictMode double-invokes effects
// and any accidental double-mount must share ONE observer/overlay. We refcount
// so the controller starts on the first mount and only tears down on the last.
// ---------------------------------------------------------------------------
interface GenieController {
  refs: number;
  observer: MutationObserver | null;
  overlay: HTMLElement | null;
  running: Set<Animation>; // every flight + pulse anim, cancelled on teardown
  flown: WeakSet<Element>; // toasts already processed (never fly twice)
  disposed: boolean;
}
let controller: GenieController | null = null;

function getOverlay(ctrl: GenieController): HTMLElement {
  if (ctrl.overlay && ctrl.overlay.isConnected) return ctrl.overlay;
  let el = document.getElementById(OVERLAY_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = OVERLAY_ID;
    el.setAttribute("aria-hidden", "true");
    Object.assign(el.style, {
      position: "fixed",
      inset: "0",
      margin: "0",
      padding: "0",
      pointerEvents: "none",
      zIndex: "2147483646",
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(el);
  }
  ctrl.overlay = el;
  return el;
}

// Short squash-stretch on the bell + a portaled absorb-ring to "catch" the toast.
function pulseBell(ctrl: GenieController): void {
  if (ctrl.disposed || prefersReducedMotion()) return;
  // Re-query live: a route change mid-flight may have replaced the topbar.
  const bell = document.querySelector<HTMLElement>(BELL_SELECTOR);
  if (!bell || !bell.isConnected) return;
  const r = bell.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return;

  try {
    const a = bell.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.24)", offset: 0.4 },
        { transform: "scale(0.94)", offset: 0.7 },
        { transform: "scale(1)" },
      ],
      { duration: PULSE_MS, easing: "cubic-bezier(0.34, 1.56, 0.64, 1)" }
    );
    ctrl.running.add(a);
    a.finished.catch(() => {}).finally(() => ctrl.running.delete(a));
  } catch {
    /* WAAPI unsupported -> skip pulse */
  }

  // Absorb-ring at the bell center. Only mount it inside the live overlay so
  // nothing is parked on <body> after teardown.
  if (!ctrl.overlay || !ctrl.overlay.isConnected) return;
  try {
    const ring = document.createElement("span");
    ring.setAttribute("aria-hidden", "true");
    Object.assign(ring.style, {
      position: "fixed",
      left: `${r.left + r.width / 2}px`,
      top: `${r.top + r.height / 2}px`,
      width: "14px",
      height: "14px",
      marginLeft: "-7px",
      marginTop: "-7px",
      borderRadius: "9999px",
      background: "var(--gt-primary, #0f2d1f)",
      opacity: "0.5",
      pointerEvents: "none",
    } as Partial<CSSStyleDeclaration>);
    ctrl.overlay.appendChild(ring);
    const ra = ring.animate(
      [
        { transform: "scale(0.4)", opacity: 0.5 },
        { transform: "scale(2.4)", opacity: 0 },
      ],
      { duration: 420, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
    );
    ctrl.running.add(ra);
    const drop = () => {
      ctrl.running.delete(ra);
      ring.remove();
    };
    ra.finished.then(drop).catch(drop);
  } catch {
    /* no-op */
  }
}

// Run the genie flight for one dismissed toast <li>.
function flyToast(toast: HTMLElement, ctrl: GenieController): void {
  // Never process the same node twice (StrictMode, repeated mutation records).
  if (ctrl.flown.has(toast) || toast.getAttribute(FLOWN_ATTR) === "1") return;
  // Leave swipe-dismiss to Sonner's own swipe-out gesture animation.
  if (toast.getAttribute("data-swipe-out") === "true") return;
  ctrl.flown.add(toast);
  toast.setAttribute(FLOWN_ATTR, "1");

  const bell = document.querySelector<HTMLElement>(BELL_SELECTOR);
  // No bell on this page (e.g. /login) -> let Sonner fade normally.
  // (Bail BEFORE hiding the original so dismissal looks normal.)
  if (!bell) return;

  const toastRect = toast.getBoundingClientRect();
  if (toastRect.width < 1 || toastRect.height < 1) return;

  const bellRect = bell.getBoundingClientRect();
  if (bellRect.width < 1 || bellRect.height < 1) return;

  const endCx = bellRect.left + bellRect.width / 2;
  const endCy = bellRect.top + bellRect.height / 2;
  // Skip if the bell is off-screen or covered (e.g. the bell panel is open and
  // its backdrop sits over the button) — flying to a hidden target looks wrong.
  if (!isPointOnElement(bell, endCx, endCy)) return;

  // Hide the original immediately so Sonner's exit transition doesn't show
  // alongside the clone. display:none vacates its stack slot atomically so the
  // remaining toasts reflow in one frame instead of sliding under the clone.
  toast.style.display = "none";

  // ---- Build a fixed clone positioned exactly over the original. ----------
  const overlay = getOverlay(ctrl);
  const wrapper = document.createElement("div");
  wrapper.setAttribute("data-genie-clone", "");
  wrapper.setAttribute("aria-hidden", "true");
  Object.assign(wrapper.style, {
    position: "fixed",
    left: `${toastRect.left}px`,
    top: `${toastRect.top}px`,
    width: `${toastRect.width}px`,
    height: `${toastRect.height}px`,
    margin: "0",
    padding: "0",
    pointerEvents: "none",
    willChange: "transform, opacity",
    transformOrigin: "center center",
    boxSizing: "border-box",
  } as Partial<CSSStyleDeclaration>);

  const clone = toast.cloneNode(true) as HTMLElement;
  // Neutralize the clone's own removal/transition attrs so it can't re-trigger
  // us or run Sonner's exit transition.
  clone.removeAttribute("data-removed");
  clone.removeAttribute("data-swipe-out");
  clone.removeAttribute(FLOWN_ATTR);
  Object.assign(clone.style, {
    opacity: "1",
    visibility: "visible",
    display: "flex",
    transition: "none",
    animation: "none",
    transform: "none",
    margin: "0",
    position: "static",
    width: "100%",
    height: "100%",
    maxWidth: "none",
    pointerEvents: "none",
  } as Partial<CSSStyleDeclaration>);
  wrapper.appendChild(clone);
  overlay.appendChild(wrapper);

  // ---- Geometry: toast center -> bell center, bowed control point. --------
  const startCx = toastRect.left + toastRect.width / 2;
  const startCy = toastRect.top + toastRect.height / 2;
  const p0 = { x: startCx, y: startCy };
  const p1 = { x: endCx, y: endCy };
  const dx = endCx - startCx;
  const dy = endCy - startCy;
  // Bow the arc toward the travel direction (works for left- or right-side bells).
  const bowDir = dx === 0 ? 1 : Math.sign(dx);
  const pc = {
    x: (startCx + endCx) / 2 + Math.abs(dx) * ARC_BOW * bowDir,
    y: (startCy + endCy) / 2 - Math.abs(dy) * 0.25,
  };

  // Final scale ~ bell footprint (clamped so huge/tiny toasts still converge).
  const targetScale = Math.max(0.05, Math.min(0.22, bellRect.width / toastRect.width));

  // ---- Sample the bezier into keyframes. ----------------------------------
  const frames: Keyframe[] = [];
  for (let i = 0; i <= ARC_SAMPLES; i++) {
    const t = i / ARC_SAMPLES;
    const pt = quadraticPoint(t, p0, pc, p1);
    const tx = pt.x - startCx;
    const ty = pt.y - startCy;

    const baseScale = 1 + (targetScale - 1) * t; // uniform shrink over flight
    // The "suck": in the back half, scaleX collapses faster than scaleY so the
    // toast tapers into a thin sliver (genie neck) before vanishing.
    const backHalf = Math.max(0, (t - 0.5) / 0.5); // 0 until midpoint, ->1
    const xTaper = 1 - 0.8 * backHalf * backHalf;   // ease-in extra X collapse
    const sx = baseScale * xTaper;
    const sy = baseScale;

    const radius = 12 + 34 * t;                      // morph toward a blob/pill
    const opacity = t < 0.7 ? 1 : Math.max(0, 1 - (t - 0.7) / 0.3); // late fade

    frames.push({
      transform: `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})`,
      borderRadius: `${radius}px`,
      opacity,
      offset: t,
    });
  }

  const cleanupClone = () => {
    if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
  };

  let anim: Animation;
  try {
    anim = wrapper.animate(frames, {
      duration: FLIGHT_MS,
      easing: "cubic-bezier(0.4, 0.0, 0.6, 1)", // accelerate into the bell
      fill: "forwards",
    });
  } catch {
    // WAAPI unsupported -> drop the clone; original is already hidden/removed.
    cleanupClone();
    return;
  }

  ctrl.running.add(anim);
  anim.finished
    .then(() => {
      cleanupClone();
      if (!ctrl.disposed) pulseBell(ctrl);
    })
    .catch(cleanupClone) // cancelled (teardown) -> just remove the clone
    .finally(() => ctrl.running.delete(anim));
}

// ---------------------------------------------------------------------------
// Controller lifecycle
// ---------------------------------------------------------------------------
function startController(): GenieController {
  if (controller) {
    controller.refs += 1;
    return controller;
  }
  const ctrl: GenieController = {
    refs: 1,
    observer: null,
    overlay: null,
    running: new Set(),
    flown: new WeakSet(),
    disposed: false,
  };

  // Sonner mounts [data-sonner-toaster] lazily and UNMOUNTS it whenever no
  // toasts remain, mounting a fresh one for the next batch. So we cannot
  // attach to "the toaster" once — we observe <body> permanently, filtered to
  // the single `data-removed` attribute (cheap: fires only when a toast
  // dismisses), which survives every toaster mount/unmount cycle.
  const onMutations: MutationCallback = (records) => {
    for (const rec of records) {
      const el = rec.target;
      if (
        el instanceof HTMLElement &&
        el.matches(TOAST_SELECTOR) &&
        el.getAttribute("data-removed") === "true" &&
        el.getAttribute("data-swipe-out") !== "true"
      ) {
        flyToast(el, ctrl);
      }
    }
  };

  ctrl.observer = new MutationObserver(onMutations);
  ctrl.observer.observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ["data-removed"],
  });

  controller = ctrl;
  return ctrl;
}

function stopController(): void {
  if (!controller) return;
  controller.refs -= 1;
  if (controller.refs > 0) return; // another mount still alive

  const ctrl = controller;
  ctrl.disposed = true;
  ctrl.observer?.disconnect();
  // Cancel in-flight animations; each cancel() rejects .finished -> its
  // .catch(cleanupClone) removes that clone. Then remove the overlay wholesale.
  ctrl.running.forEach((a) => {
    try {
      a.cancel();
    } catch {
      /* noop */
    }
  });
  ctrl.running.clear();
  if (ctrl.overlay?.parentNode) ctrl.overlay.parentNode.removeChild(ctrl.overlay);
  ctrl.overlay = null;
  controller = null;
}

// ---------------------------------------------------------------------------
// Component (renders nothing)
// ---------------------------------------------------------------------------
export default function NotificationGenie(): null {
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      typeof MutationObserver === "undefined"
    ) {
      return;
    }

    const mq =
      typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION) : null;
    let started = false;
    const sync = () => {
      const reduce = !!mq?.matches;
      if (reduce && started) {
        stopController();
        started = false;
      } else if (!reduce && !started) {
        startController();
        started = true;
      }
    };
    sync();
    mq?.addEventListener?.("change", sync);

    return () => {
      mq?.removeEventListener?.("change", sync);
      if (started) stopController();
    };
  }, []);

  return null;
}
